<template>
  <div
    v-if="isOpen"
    class="fixed inset-0 z-[200] flex items-start justify-center px-4 py-8 overflow-y-auto bg-black/50 backdrop-blur-sm"
    @click.self="closeModal"
  >
    <div class="bg-white rounded-xl shadow-2xl w-full max-w-4xl transform transition-all animate-in fade-in zoom-in duration-300 text-left relative overflow-visible">
      <div class="px-6 py-4 bg-[#0d4169] text-white flex items-center justify-between rounded-t-xl">
        <h2 class="text-lg font-bold m-0">
          {{ modalTitle }}
        </h2>
        <button type="button" class="p-1 hover:bg-white/10 rounded-full transition-colors text-white" @click="closeModal">
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <form class="p-6 sm:p-8 relative" @submit.prevent="handleSubmit">
        <div
          v-if="formDataLoading"
          class="absolute inset-0 z-10 flex items-center justify-center bg-white/70 rounded-b-xl"
        >
          <div class="h-8 w-8 border-4 border-[#0d4169]/30 border-t-transparent rounded-full animate-spin"></div>
        </div>
        <p v-if="formError" class="mb-3 text-xs text-red-600">{{ formError }}</p>

        <div class="mb-5 flex flex-col sm:flex-row gap-4">
          <div class="shrink-0">
            <div class="w-24 h-24 rounded-lg border border-slate-200 bg-slate-50 overflow-hidden flex items-center justify-center">
              <img
                v-if="form.photo"
                :src="resolveCompanyLogoUrl(form.photo)"
                alt="Employee"
                class="w-full h-full object-cover"
              >
              <span v-else class="text-[0.65rem] text-slate-400 px-2 text-center">No photo</span>
            </div>
          </div>
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-3 flex-1 min-w-0">
            <div class="flex flex-col gap-1.5">
              <label class="text-[0.85rem] font-semibold text-[#1e293b]">Date of Employment</label>
              <input
                :value="form.start_date_display"
                type="text"
                readonly
                class="px-3 py-2 border border-[#cbd5e1] rounded-md outline-none text-sm bg-slate-50 text-slate-600"
              >
            </div>
            <div class="flex flex-col gap-1.5">
              <label class="text-[0.85rem] font-semibold text-[#1e293b]">Employee ID No.</label>
              <input
                v-model="form.emp_idno"
                type="text"
                readonly
                class="px-3 py-2 border border-[#cbd5e1] rounded-md outline-none text-sm bg-slate-50 text-slate-600"
              >
            </div>
            <div class="flex flex-col gap-1.5">
              <label class="text-[0.85rem] font-semibold text-[#1e293b]">Full Name</label>
              <input
                v-model="form.full_name"
                type="text"
                readonly
                class="px-3 py-2 border border-[#cbd5e1] rounded-md outline-none text-sm bg-slate-50 text-slate-600"
              >
            </div>
            <div class="flex flex-col gap-1.5">
              <label class="text-[0.85rem] font-semibold text-[#1e293b]">Service Month</label>
              <input
                :value="form.service_month"
                type="text"
                readonly
                class="px-3 py-2 border border-[#cbd5e1] rounded-md outline-none text-sm bg-slate-50 text-slate-600"
              >
            </div>
          </div>
        </div>

        <div class="mb-2">
          <span class="text-[0.75rem] font-bold capitalize text-[#0d4169] bg-slate-100 px-2 py-1 rounded">Leave Request</span>
        </div>
        <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-x-3 gap-y-3 mb-5">
          <div class="flex flex-col gap-1.5">
            <label class="text-[0.85rem] font-semibold text-[#1e293b]">Total Leave</label>
            <input
              :value="form.total_leave"
              type="text"
              readonly
              class="px-3 py-2 border border-[#cbd5e1] rounded-md outline-none text-sm bg-slate-50 text-slate-600"
            >
          </div>
          <div class="flex flex-col gap-1.5">
            <label class="text-[0.85rem] font-semibold text-[#1e293b]">Used Leave</label>
            <input
              :value="form.used_leave"
              type="text"
              readonly
              class="px-3 py-2 border border-[#cbd5e1] rounded-md outline-none text-sm bg-slate-50 text-slate-600"
            >
          </div>
          <div class="flex flex-col gap-1.5">
            <label class="text-[0.85rem] font-semibold text-[#1e293b]">Current Available</label>
            <input
              :value="form.current_available"
              type="text"
              readonly
              class="px-3 py-2 border border-[#cbd5e1] rounded-md outline-none text-sm bg-slate-50 text-slate-600"
            >
          </div>

          <div class="flex flex-col gap-1.5">
            <label class="text-[0.85rem] font-semibold text-[#1e293b]">Leaving Type</label>
            <select
              v-model="form.leave_type_id"
              required
              class="px-3 py-2 border border-[#cbd5e1] rounded-md outline-none focus:border-[#2563eb] transition-all text-sm bg-white cursor-pointer"
              @change="onLeaveTypeChange"
            >
              <option value="" disabled>Select</option>
              <option
                v-for="item in leaveTypes"
                :key="item.id"
                :value="String(item.id)"
              >
                {{ item.leaveTypeName }}
              </option>
            </select>
          </div>

          <div class="flex flex-col gap-1.5">
            <label class="text-[0.85rem] font-semibold text-[#1e293b]">Decrease Per/Year</label>
            <input
              v-model="form.leave_decrease"
              type="text"
              readonly
              class="px-3 py-2 border border-[#cbd5e1] rounded-md outline-none text-sm bg-slate-50 text-slate-600"
            >
          </div>

          <div class="flex flex-col gap-1.5">
            <label class="text-[0.85rem] font-semibold text-[#1e293b]">Reason / Evidence</label>
            <label
              for="annual-leave-evidence-upload"
              class="flex items-center gap-3 bg-white border border-dashed border-[#cbd5e1] rounded-lg p-2 min-h-[50px] transition-all group"
              :class="evidenceEnabled
                ? 'cursor-pointer hover:border-[#2563eb]'
                : 'opacity-60 cursor-not-allowed bg-slate-50'"
            >
              <div v-if="form.evidence_attach" class="relative shrink-0">
                <img
                  :src="resolveCompanyLogoUrl(form.evidence_attach)"
                  class="w-10 h-10 object-contain rounded bg-slate-50 border border-slate-100"
                >
                <button
                  v-if="evidenceEnabled"
                  type="button"
                  class="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full w-4 h-4 flex items-center justify-center text-[10px] shadow-sm hover:bg-red-600 transition-colors z-10"
                  @click.prevent="form.evidence_attach = ''"
                >✕</button>
              </div>
              <div
                v-else
                class="w-10 h-10 rounded bg-slate-50 border border-dashed border-slate-300 flex items-center justify-center text-slate-300 shrink-0"
              >
                <svg v-if="!uploadingEvidence" class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path></svg>
                <svg v-else class="w-5 h-5 animate-spin text-[#2563eb]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
              </div>
              <div class="flex-1 leading-tight overflow-hidden">
                <p class="text-[0.75rem] font-bold text-[#0d4169] truncate">
                  <span v-if="uploadingEvidence">Uploading...</span>
                  <span v-else-if="!evidenceEnabled">Not required</span>
                  <span v-else>{{ form.evidence_attach ? 'Change Evidence' : 'Choose Evidence' }}</span>
                </p>
                <p class="text-[0.65rem] text-slate-400 mt-0.5">PNG, JPG, max 3MB</p>
              </div>
              <input
                id="annual-leave-evidence-upload"
                type="file"
                class="hidden"
                accept="image/*"
                :disabled="!evidenceEnabled"
                @change="handleEvidenceUpload"
              >
            </label>
          </div>

          <div class="flex flex-col gap-1.5">
            <label class="text-[0.85rem] font-semibold text-[#1e293b]">Request Date</label>
            <input
              v-model="form.request_date"
              type="date"
              required
              class="px-3 py-2 border border-[#cbd5e1] rounded-md outline-none focus:border-[#2563eb] transition-all text-sm"
            >
          </div>
          <div class="flex flex-col gap-1.5">
            <label class="text-[0.85rem] font-semibold text-[#1e293b]">Leave From</label>
            <input
              v-model="form.leave_from"
              type="date"
              :min="leaveFromMin"
              required
              class="px-3 py-2 border border-[#cbd5e1] rounded-md outline-none focus:border-[#2563eb] transition-all text-sm"
              @change="onLeaveFromChange"
            >
          </div>
          <div class="flex flex-col gap-1.5">
            <label class="text-[0.85rem] font-semibold text-[#1e293b]">Leave To</label>
            <input
              v-model="form.leave_to"
              type="date"
              :min="form.leave_from || leaveFromMin"
              required
              class="px-3 py-2 border border-[#cbd5e1] rounded-md outline-none focus:border-[#2563eb] transition-all text-sm"
            >
          </div>
        </div>

        <div class="mt-8 pt-6 border-t border-slate-100 flex justify-end gap-2 items-center">
          <button
            type="button"
            class="px-4 py-2 text-sm font-semibold text-slate-500 hover:text-slate-800 transition-colors"
            @click="closeModal"
          >
            Cancel
          </button>
          <button
            type="submit"
            :disabled="formDataLoading || saving || uploadingEvidence"
            class="px-6 py-2 bg-[#0d4169] text-white text-sm font-bold rounded-md shadow-md hover:bg-[#0a3354] transition-all active:transform active:scale-95 whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            <div
              v-if="saving"
              class="h-4 w-4 border-2 border-white/30 border-t-transparent rounded-full animate-spin"
            ></div>
            <span v-if="!saving">{{ isApproveMode ? 'Approve' : 'Save' }}</span>
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
import { computed, defineEmits, defineProps, reactive, ref, watch } from 'vue'
import { apiFetch, normalizeUploadValue, resolveCompanyLogoUrl } from '../../../utils/api'

const props = defineProps({
  isOpen: Boolean,
  employee: {
    type: Object,
    default: null,
  },
  mode: {
    type: String,
    default: 'create',
  },
  leaveId: {
    type: [Number, String],
    default: null,
  },
})

const emit = defineEmits(['close', 'saved'])

const leaveTypes = ref([])
const formDataLoading = ref(false)
const formError = ref('')
const saving = ref(false)
const uploadingEvidence = ref(false)

const isEditMode = computed(() => String(props.mode || '').toLowerCase() === 'edit')
const isApproveMode = computed(() => String(props.mode || '').toLowerCase() === 'approve')
const isExistingRequestMode = computed(() => isEditMode.value || isApproveMode.value)
const modalTitle = computed(() => {
  if (isApproveMode.value) return 'Approve Annual Leave Request'
  if (isEditMode.value) return 'Edit Annual Leave Request'
  return 'Request New Annual Leave'
})

const form = reactive({
  emp_idno: '',
  full_name: '',
  photo: '',
  start_date: '',
  start_date_display: '',
  service_month: '',
  total_leave: 0,
  used_leave: 0,
  current_available: 0,
  leave_type_id: '',
  leave_decrease: '',
  condition_evidence: '',
  evidence_attach: '',
  request_date: '',
  leave_from: '',
  leave_to: '',
})

const evidenceEnabled = computed(() => form.condition_evidence === 'Yes')

const todayIso = computed(() => {
  const d = new Date()
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
})

const leaveFromMin = computed(() => {
  if (
    isExistingRequestMode.value &&
    form.leave_from &&
    /^\d{4}-\d{2}-\d{2}$/.test(form.leave_from) &&
    form.leave_from < todayIso.value
  ) {
    return form.leave_from
  }
  return todayIso.value
})

const requestDays = computed(() => {
  if (!form.leave_from || !form.leave_to) return 0
  if (!/^\d{4}-\d{2}-\d{2}$/.test(form.leave_from) || !/^\d{4}-\d{2}-\d{2}$/.test(form.leave_to)) return 0
  const [y1, m1, d1] = form.leave_from.split('-').map(Number)
  const [y2, m2, d2] = form.leave_to.split('-').map(Number)
  const from = new Date(y1, m1 - 1, d1)
  const to = new Date(y2, m2 - 1, d2)
  const diff = Math.round((to - from) / 86400000) + 1
  return diff > 0 ? diff : 0
})

function formatDisplayDate(value) {
  if (!value) return '—'
  const s = String(value)
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
    const [y, m, d] = s.slice(0, 10).split('-')
    return `${d}/${m}/${y}`
  }
  return s
}

/** Normalize API date values to YYYY-MM-DD for <input type="date"> (avoid UTC -1 day). */
function toDateInputValue(value) {
  if (!value) return ''
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    const y = value.getFullYear()
    const m = String(value.getMonth() + 1).padStart(2, '0')
    const d = String(value.getDate()).padStart(2, '0')
    return `${y}-${m}-${d}`
  }
  const s = String(value).trim()
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s
  if (/^\d{4}-\d{2}-\d{2}T/.test(s)) {
    const dt = new Date(s)
    if (!Number.isNaN(dt.getTime())) {
      const y = dt.getFullYear()
      const m = String(dt.getMonth() + 1).padStart(2, '0')
      const d = String(dt.getDate()).padStart(2, '0')
      return `${y}-${m}-${d}`
    }
  }
  return s.length >= 10 ? s.slice(0, 10) : s
}

function onLeaveFromChange() {
  if (form.leave_from && form.leave_to && form.leave_to < form.leave_from) {
    form.leave_to = form.leave_from
  }
}

function resetForm() {
  form.emp_idno = ''
  form.full_name = ''
  form.photo = ''
  form.start_date = ''
  form.start_date_display = ''
  form.service_month = ''
  form.total_leave = 0
  form.used_leave = 0
  form.current_available = 0
  form.leave_type_id = ''
  form.leave_decrease = ''
  form.condition_evidence = ''
  form.evidence_attach = ''
  form.request_date = ''
  form.leave_from = ''
  form.leave_to = ''
  formError.value = ''
}

function hydrateFromEmployee(employee) {
  if (!employee) {
    resetForm()
    return
  }
  form.emp_idno = employee.id != null ? String(employee.id) : ''
  form.full_name = employee.full_name || ''
  form.photo = normalizeUploadValue(employee.photo || '')
  form.start_date = employee.start_date || ''
  form.start_date_display = formatDisplayDate(employee.start_date)
  form.service_month =
    employee.service_month === null || employee.service_month === undefined || employee.service_month === ''
      ? '—'
      : String(employee.service_month)
  form.total_leave = Number(employee.total_leave ?? 0) || 0
  form.used_leave = Number(employee.used_leave ?? 0) || 0
  form.current_available = Number(employee.current_available ?? 0) || 0
  formError.value = ''

  if (isExistingRequestMode.value) {
    form.leave_type_id =
      employee.pending_leave_type_id != null && employee.pending_leave_type_id !== ''
        ? String(employee.pending_leave_type_id)
        : ''
    form.leave_decrease = employee.pending_leave_decrease || ''
    form.condition_evidence = ''
    form.evidence_attach = normalizeUploadValue(employee.pending_evidence_attach || '')
    form.request_date = toDateInputValue(employee.pending_request_date) || todayIso.value
    form.leave_from = toDateInputValue(employee.pending_leave_from)
    form.leave_to = toDateInputValue(employee.pending_leave_to)
  } else {
    form.leave_type_id = ''
    form.leave_decrease = ''
    form.condition_evidence = ''
    form.evidence_attach = ''
    form.request_date = todayIso.value
    form.leave_from = ''
    form.leave_to = ''
  }
}

function onLeaveTypeChange() {
  const selected = leaveTypes.value.find((row) => String(row.id) === String(form.leave_type_id))
  form.leave_decrease = selected?.decreaseFromYearlyLeave || ''
  form.condition_evidence = selected?.conditionEvidence || ''
  if (form.condition_evidence !== 'Yes') {
    form.evidence_attach = ''
  }
}

async function loadLeaveTypes() {
  formDataLoading.value = true
  formError.value = ''
  try {
    const res = await apiFetch('/hr/annual-leaving-type')
    if (!res.ok) {
      const err = await res.json().catch(() => ({}))
      throw new Error(err.message || 'Failed to load Leaving Types')
    }
    const data = await res.json().catch(() => [])
    leaveTypes.value = Array.isArray(data) ? data.filter((row) => !!row?.active) : []
  } catch (err) {
    console.error('Error loading leave types:', err)
    leaveTypes.value = []
    formError.value = err?.message || 'Failed to load Leaving Types'
  } finally {
    formDataLoading.value = false
  }
}

async function handleEvidenceUpload(event) {
  if (!evidenceEnabled.value) {
    event.target.value = ''
    return
  }
  const file = event.target.files?.[0]
  if (!file) return

  if (file.size > 3 * 1024 * 1024) {
    alert('File is too large. Max 3MB.')
    event.target.value = ''
    return
  }

  uploadingEvidence.value = true
  try {
    const formData = new FormData()
    formData.append('logo', file)
    const res = await apiFetch('/upload', {
      method: 'POST',
      body: formData,
    })
    if (res.ok) {
      const data = await res.json()
      form.evidence_attach = normalizeUploadValue(data.url)
    } else {
      throw new Error('Upload failed')
    }
  } catch (err) {
    console.error('Error uploading evidence:', err)
    alert('Failed to upload image.')
  } finally {
    uploadingEvidence.value = false
    event.target.value = ''
  }
}

watch(
  () => [props.isOpen, props.employee, props.mode, props.leaveId],
  async ([isOpen]) => {
    if (isOpen) {
      hydrateFromEmployee(props.employee)
      uploadingEvidence.value = false
      saving.value = false
      await loadLeaveTypes()
      if (isExistingRequestMode.value && form.leave_type_id) {
        onLeaveTypeChange()
      }
    } else {
      leaveTypes.value = []
      formError.value = ''
      saving.value = false
      uploadingEvidence.value = false
    }
  },
  { immediate: true }
)

function closeModal() {
  emit('close')
}

async function handleSubmit() {
  if (saving.value || formDataLoading.value || uploadingEvidence.value) return
  formError.value = ''

  const employeeId = Number(form.emp_idno)
  if (!Number.isFinite(employeeId)) {
    formError.value = 'Employee ID is required.'
    return
  }
  if (!form.leave_type_id) {
    formError.value = 'Leaving Type is required.'
    return
  }
  if (!form.request_date) {
    formError.value = 'Request Date is required.'
    return
  }
  if (!form.leave_from || !form.leave_to) {
    formError.value = 'Leave From and Leave To are required.'
    return
  }
  if (form.leave_to < form.leave_from) {
    formError.value = 'Leave To must be on or after Leave From.'
    return
  }
  if (!requestDays.value || requestDays.value < 1) {
    formError.value = 'Leave period must be at least 1 day.'
    return
  }
  if (requestDays.value > (Number(form.current_available) || 0)) {
    formError.value =
      'Leave From / Leave To difference must be less than or equal to Current Available.'
    return
  }
  if (evidenceEnabled.value && !form.evidence_attach) {
    formError.value = 'Reason / Evidence photo is required for this leave type.'
    return
  }

  const editLeaveId = Number(props.leaveId)
  if (isExistingRequestMode.value && !Number.isFinite(editLeaveId)) {
    formError.value = isApproveMode.value
      ? 'Leave request id is required for approve.'
      : 'Leave request id is required for edit.'
    return
  }

  if (isApproveMode.value) {
    const ok = window.confirm('Approve this annual leave request?')
    if (!ok) return

    saving.value = true
    try {
      const res = await apiFetch(`/hr/employee/annual-leaving/${editLeaveId}/approve`, {
        method: 'PATCH',
      })
      if (!res.ok) {
        const err = await res.json().catch(() => ({}))
        throw new Error(err.message || 'Failed to approve leave request')
      }
      const data = await res.json().catch(() => ({}))
      emit('saved', data.leave || { id: editLeaveId })
      emit('close')
    } catch (err) {
      console.error('Error approving annual leave request:', err)
      formError.value = err?.message || 'Failed to approve leave request'
    } finally {
      saving.value = false
    }
    return
  }

  saving.value = true
  try {
    const payload = {
      userId: employeeId,
      leaveTypeId: Number(form.leave_type_id),
      leaveDecrease: form.leave_decrease || null,
      currentAvailable: Number(form.current_available) || 0,
      requestDate: form.request_date,
      requestDays: requestDays.value,
      leaveFrom: form.leave_from,
      leaveTo: form.leave_to,
      evidenceAttach: form.evidence_attach || null,
    }

    const res = isEditMode.value
      ? await apiFetch(`/hr/employee/annual-leaving/${editLeaveId}`, {
          method: 'PUT',
          body: JSON.stringify(payload),
        })
      : await apiFetch('/hr/employee/annual-leaving', {
          method: 'POST',
          body: JSON.stringify(payload),
        })
    if (!res.ok) {
      const err = await res.json().catch(() => ({}))
      throw new Error(
        err.message ||
          (isEditMode.value
            ? 'Failed to update annual leave request'
            : 'Failed to save annual leave request')
      )
    }
    const data = await res.json().catch(() => ({}))
    emit('saved', data.leave || payload)
    emit('close')
  } catch (err) {
    console.error(
      isEditMode.value
        ? 'Error updating annual leave request:'
        : 'Error saving annual leave request:',
      err
    )
    formError.value =
      err?.message ||
      (isEditMode.value
        ? 'Failed to update annual leave request'
        : 'Failed to save annual leave request')
  } finally {
    saving.value = false
  }
}
</script>
